/**
 * Math parser — converts OMML (Office Math Markup Language) to LaTeX strings.
 *
 * OOXML stores math formulas using the `m:` namespace with elements like:
 *   - <m:f> fractions, <m:sSup> superscripts, <m:sSub> subscripts
 *   - <m:rad> radicals, <m:d> delimiters, <m:nary> n-ary operators
 *   - <m:m> matrices, <m:eqArr> equation arrays, <m:acc> accents
 *   - <m:r><m:t> math text runs
 *
 * This parser recursively walks the OMML tree and produces a LaTeX string
 * that can be rendered by KaTeX in TipTap's Mathematics extension.
 *
 * Output is used as:
 *   { type: "inlineMath", attrs: { latex: "..." } }   — for <m:oMath>
 *   { type: "blockMath",  attrs: { latex: "..." } }   — for <m:oMathPara>
 */

const M_NS = 'http://schemas.openxmlformats.org/officeDocument/2006/math';

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Convert an <m:oMath> or <m:oMathPara> element to a LaTeX string.
 *
 * For <m:oMathPara> containing multiple <m:oMath> children, they are
 * joined with `\\` (LaTeX newline) to form a multi-line equation.
 */
export function convertOmmlToLatex(element: Element): string {
  return convertElement(element).trim();
}

// ---------------------------------------------------------------------------
// Recursive element converter
// ---------------------------------------------------------------------------

/** Recursively convert an OMML element to LaTeX. */
function convertElement(el: Element): string {
  const localName = el.localName || el.nodeName.replace(/^m:/, '');

  switch (localName) {
    case 'oMathPara':
      return convertOmmlPara(el);
    case 'oMath':
      return convertChildren(el);

    // Text run
    case 'r':
      return convertMathRun(el);

    // Structures
    case 'f':
      return convertFraction(el);
    case 'sSup':
      return convertSuperscript(el);
    case 'sSub':
      return convertSubscript(el);
    case 'sSubSup':
      return convertSubSuperscript(el);
    case 'sPre':
      return convertPreSubSup(el);
    case 'rad':
      return convertRadical(el);
    case 'd':
      return convertDelimiter(el);
    case 'nary':
      return convertNary(el);
    case 'm':
      return convertMatrix(el);
    case 'eqArr':
      return convertEquationArray(el);
    case 'acc':
      return convertAccent(el);
    case 'bar':
      return convertBar(el);
    case 'func':
      return convertFunction(el);
    case 'limLow':
      return convertLowerLimit(el);
    case 'limUpp':
      return convertUpperLimit(el);
    case 'groupChr':
      return convertGroupChar(el);
    case 'borderBox':
    case 'box':
      return convertBox(el);

    // Container elements — just recurse into children
    case 'e':
    case 'num':
    case 'den':
    case 'sup':
    case 'sub':
    case 'lim':
    case 'deg':
    case 'fName':
      return convertChildren(el);

    // Property elements — skip (handled by parent)
    case 'rPr':
    case 'fPr':
    case 'sSupPr':
    case 'sSubPr':
    case 'sSubSupPr':
    case 'sPrePr':
    case 'radPr':
    case 'dPr':
    case 'naryPr':
    case 'mPr':
    case 'eqArrPr':
    case 'accPr':
    case 'barPr':
    case 'funcPr':
    case 'limLowPr':
    case 'limUppPr':
    case 'groupChrPr':
    case 'borderBoxPr':
    case 'boxPr':
    case 'ctrlPr':
    case 'oMathParaPr':
      return '';

    default:
      // Unknown element — try to extract children
      return convertChildren(el);
  }
}

/** Convert all child elements of a node. */
function convertChildren(el: Element): string {
  let result = '';
  const children = el.childNodes;
  for (let i = 0; i < children.length; i++) {
    const child = children[i];
    if (child.nodeType === 1) {
      result += convertElement(child as Element);
    }
  }
  return result;
}

// ---------------------------------------------------------------------------
// Element-specific converters
// ---------------------------------------------------------------------------

/**
 * <m:oMathPara> — display math paragraph.
 * May contain multiple <m:oMath> children (equation array style).
 */
function convertOmmlPara(el: Element): string {
  const maths = getDirectChildren(el, 'oMath');
  if (maths.length === 0) return convertChildren(el);
  if (maths.length === 1) return convertChildren(maths[0]);

  // Multiple equations → join with LaTeX newline
  const parts = maths.map((m) => convertChildren(m));
  return parts.join(' \\\\ ');
}

/** <m:r> — math run containing <m:t> text, with optional styling via <m:rPr>. */
function convertMathRun(el: Element): string {
  // Extract run properties for styling
  const rPr = getDirectChild(el, 'rPr');
  const sty = rPr ? getPropVal(rPr, 'sty') : null;
  const nor = rPr ? getPropVal(rPr, 'nor') : null;
  const scr = rPr ? getPropVal(rPr, 'scr') : null;

  // Extract text content
  let text = '';
  const children = el.childNodes;
  for (let i = 0; i < children.length; i++) {
    const child = children[i];
    if (child.nodeType === 1) {
      const childEl = child as Element;
      const ln = childEl.localName || childEl.nodeName.replace(/^m:/, '');
      if (ln === 't') {
        text += childEl.textContent || '';
      }
    }
  }

  const mapped = mapSpecialChars(text);

  // Apply style wrapping
  if (nor === '1' || nor === 'on' || nor === 'true') {
    return `\\text{${mapped}}`;
  }
  if (scr === 'double-struck') return `\\mathbb{${mapped}}`;
  if (scr === 'script') return `\\mathcal{${mapped}}`;
  if (scr === 'fraktur') return `\\mathfrak{${mapped}}`;
  if (scr === 'sans-serif') return `\\mathsf{${mapped}}`;
  if (scr === 'monospace') return `\\mathtt{${mapped}}`;

  if (sty === 'b') return `\\mathbf{${mapped}}`;
  if (sty === 'bi') return `\\boldsymbol{${mapped}}`;
  if (sty === 'p') return `\\mathrm{${mapped}}`;
  // sty="i" is default math italic — no wrapper needed

  return mapped;
}

/** <m:f> — fraction: \frac{num}{den} */
function convertFraction(el: Element): string {
  const fPr = getDirectChild(el, 'fPr');
  const type = fPr ? getPropVal(fPr, 'type') : null;

  const num = getDirectChild(el, 'num');
  const den = getDirectChild(el, 'den');
  const numLatex = num ? convertChildren(num) : '';
  const denLatex = den ? convertChildren(den) : '';

  if (type === 'lin') {
    // Linear fraction: a/b
    return `{${numLatex}}/{${denLatex}}`;
  }
  if (type === 'skw') {
    // Skewed fraction
    return `{}^{${numLatex}}\\!/_{${denLatex}}`;
  }
  // Default: stacked fraction
  return `\\frac{${numLatex}}{${denLatex}}`;
}

/** <m:sSup> — superscript: base^{sup} */
function convertSuperscript(el: Element): string {
  const base = getDirectChild(el, 'e');
  const sup = getDirectChild(el, 'sup');
  const baseLatex = base ? convertChildren(base) : '';
  const supLatex = sup ? convertChildren(sup) : '';
  return `{${baseLatex}}^{${supLatex}}`;
}

/** <m:sSub> — subscript: base_{sub} */
function convertSubscript(el: Element): string {
  const base = getDirectChild(el, 'e');
  const sub = getDirectChild(el, 'sub');
  const baseLatex = base ? convertChildren(base) : '';
  const subLatex = sub ? convertChildren(sub) : '';
  return `{${baseLatex}}_{${subLatex}}`;
}

/** <m:sSubSup> — sub-superscript: base_{sub}^{sup} */
function convertSubSuperscript(el: Element): string {
  const base = getDirectChild(el, 'e');
  const sub = getDirectChild(el, 'sub');
  const sup = getDirectChild(el, 'sup');
  const baseLatex = base ? convertChildren(base) : '';
  const subLatex = sub ? convertChildren(sub) : '';
  const supLatex = sup ? convertChildren(sup) : '';
  return `{${baseLatex}}_{${subLatex}}^{${supLatex}}`;
}

/** <m:sPre> — pre-sub-superscript: _{sub}^{sup}base */
function convertPreSubSup(el: Element): string {
  const sub = getDirectChild(el, 'sub');
  const sup = getDirectChild(el, 'sup');
  const e = getDirectChild(el, 'e');
  const subLatex = sub ? convertChildren(sub) : '';
  const supLatex = sup ? convertChildren(sup) : '';
  const eLatex = e ? convertChildren(e) : '';
  return `{}_{${subLatex}}^{${supLatex}}{${eLatex}}`;
}

/** <m:rad> — radical: \sqrt[deg]{e} or \sqrt{e} */
function convertRadical(el: Element): string {
  const radPr = getDirectChild(el, 'radPr');
  const degHide = radPr ? getPropVal(radPr, 'degHide') : null;

  const deg = getDirectChild(el, 'deg');
  const e = getDirectChild(el, 'e');
  const eLatex = e ? convertChildren(e) : '';

  if (degHide === '1' || degHide === 'on' || degHide === 'true') {
    return `\\sqrt{${eLatex}}`;
  }

  const degLatex = deg ? convertChildren(deg).trim() : '';
  if (degLatex && degLatex !== '2') {
    return `\\sqrt[${degLatex}]{${eLatex}}`;
  }
  return `\\sqrt{${eLatex}}`;
}

/** <m:d> — delimiter: \left( ... \right) */
function convertDelimiter(el: Element): string {
  const dPr = getDirectChild(el, 'dPr');
  let begChar = dPr ? getPropVal(dPr, 'begChr') : null;
  let endChar = dPr ? getPropVal(dPr, 'endChr') : null;
  const sepChar = dPr ? getPropVal(dPr, 'sepChr') : null;

  // Defaults
  if (begChar === null) begChar = '(';
  if (endChar === null) endChar = ')';

  const elements = getDirectChildren(el, 'e');
  const parts = elements.map((e) => convertChildren(e));
  const separator = sepChar || ',';
  const inner = parts.join(` ${separator} `);

  const leftDelim = mapDelimiter(begChar);
  const rightDelim = mapDelimiter(endChar);

  return `\\left${leftDelim} ${inner} \\right${rightDelim}`;
}

/** <m:nary> — n-ary operator: \sum, \int, \prod, etc. */
function convertNary(el: Element): string {
  const naryPr = getDirectChild(el, 'naryPr');
  const chr = naryPr ? getPropVal(naryPr, 'chr') : null;
  const subHide = naryPr ? getPropVal(naryPr, 'subHide') : null;
  const supHide = naryPr ? getPropVal(naryPr, 'supHide') : null;

  const sub = getDirectChild(el, 'sub');
  const sup = getDirectChild(el, 'sup');
  const e = getDirectChild(el, 'e');

  const operator = mapNaryOperator(chr || '\u222B'); // default: integral
  const subLatex = sub ? convertChildren(sub).trim() : '';
  const supLatex = sup ? convertChildren(sup).trim() : '';
  const eLatex = e ? convertChildren(e) : '';

  let result = operator;
  if (subLatex && subHide !== '1') result += `_{${subLatex}}`;
  if (supLatex && supHide !== '1') result += `^{${supLatex}}`;
  result += ` ${eLatex}`;
  return result;
}

/** <m:m> — matrix */
function convertMatrix(el: Element): string {
  const rows = getDirectChildren(el, 'mr');
  const rowStrings = rows.map((row) => {
    const cells = getDirectChildren(row, 'e');
    return cells.map((cell) => convertChildren(cell)).join(' & ');
  });
  return `\\begin{matrix} ${rowStrings.join(' \\\\ ')} \\end{matrix}`;
}

/** <m:eqArr> — equation array */
function convertEquationArray(el: Element): string {
  const equations = getDirectChildren(el, 'e');
  const eqStrings = equations.map((eq) => convertChildren(eq));
  if (eqStrings.length <= 1) return eqStrings[0] || '';
  return `\\begin{aligned} ${eqStrings.join(' \\\\ ')} \\end{aligned}`;
}

/** <m:acc> — accent: \hat{x}, \tilde{x}, etc. */
function convertAccent(el: Element): string {
  const accPr = getDirectChild(el, 'accPr');
  const chr = accPr ? getPropVal(accPr, 'chr') : null;
  const e = getDirectChild(el, 'e');
  const eLatex = e ? convertChildren(e) : '';

  const accentCmd = mapAccentChar(chr || '\u0302'); // default: hat
  return `${accentCmd}{${eLatex}}`;
}

/** <m:bar> — overline/underline */
function convertBar(el: Element): string {
  const barPr = getDirectChild(el, 'barPr');
  const pos = barPr ? getPropVal(barPr, 'pos') : null;
  const e = getDirectChild(el, 'e');
  const eLatex = e ? convertChildren(e) : '';

  if (pos === 'bot') {
    return `\\underline{${eLatex}}`;
  }
  return `\\overline{${eLatex}}`;
}

/** <m:func> — named function: sin, cos, log, etc. */
function convertFunction(el: Element): string {
  const fName = getDirectChild(el, 'fName');
  const e = getDirectChild(el, 'e');
  const nameLatex = fName ? convertChildren(fName).trim() : '';
  const eLatex = e ? convertChildren(e) : '';

  // Check if it's a known LaTeX function name
  const knownFunc = mapFunctionName(nameLatex);
  if (knownFunc) {
    return `${knownFunc} ${eLatex}`;
  }
  return `\\operatorname{${nameLatex}} ${eLatex}`;
}

/** <m:limLow> — lower limit (e.g., lim with subscript) */
function convertLowerLimit(el: Element): string {
  const e = getDirectChild(el, 'e');
  const lim = getDirectChild(el, 'lim');
  const eLatex = e ? convertChildren(e) : '';
  const limLatex = lim ? convertChildren(lim) : '';
  return `{${eLatex}}_{${limLatex}}`;
}

/** <m:limUpp> — upper limit */
function convertUpperLimit(el: Element): string {
  const e = getDirectChild(el, 'e');
  const lim = getDirectChild(el, 'lim');
  const eLatex = e ? convertChildren(e) : '';
  const limLatex = lim ? convertChildren(lim) : '';
  return `{${eLatex}}^{${limLatex}}`;
}

/** <m:groupChr> — grouping character (overbrace, underbrace, etc.) */
function convertGroupChar(el: Element): string {
  const pr = getDirectChild(el, 'groupChrPr');
  const chr = pr ? getPropVal(pr, 'chr') : null;
  const pos = pr ? getPropVal(pr, 'pos') : null;
  const e = getDirectChild(el, 'e');
  const eLatex = e ? convertChildren(e) : '';

  if (chr === '\u23DF' || pos === 'bot') {
    return `\\underbrace{${eLatex}}`;
  }
  if (chr === '\u23DE' || pos === 'top') {
    return `\\overbrace{${eLatex}}`;
  }
  return eLatex;
}

/** <m:borderBox> or <m:box> — boxed content (just pass through) */
function convertBox(el: Element): string {
  const e = getDirectChild(el, 'e');
  return e ? convertChildren(e) : '';
}

// ---------------------------------------------------------------------------
// XML helpers
// ---------------------------------------------------------------------------

/** Get the first direct child element with a given local name. */
function getDirectChild(parent: Element, name: string): Element | null {
  const children = parent.childNodes;
  for (let i = 0; i < children.length; i++) {
    const child = children[i];
    if (child.nodeType === 1) {
      const el = child as Element;
      const ln = el.localName || el.nodeName.replace(/^m:/, '');
      if (ln === name) return el;
    }
  }
  return null;
}

/** Get all direct child elements with a given local name. */
function getDirectChildren(parent: Element, name: string): Element[] {
  const result: Element[] = [];
  const children = parent.childNodes;
  for (let i = 0; i < children.length; i++) {
    const child = children[i];
    if (child.nodeType === 1) {
      const el = child as Element;
      const ln = el.localName || el.nodeName.replace(/^m:/, '');
      if (ln === name) result.push(el);
    }
  }
  return result;
}

/** Get the val attribute of a named child property element. */
function getPropVal(
  propsEl: Element,
  propName: string
): string | null {
  const children = propsEl.childNodes;
  for (let i = 0; i < children.length; i++) {
    const child = children[i];
    if (child.nodeType === 1) {
      const el = child as Element;
      const ln = el.localName || el.nodeName.replace(/^m:/, '');
      if (ln === propName) {
        return (
          el.getAttributeNS(M_NS, 'val') ||
          el.getAttribute('m:val') ||
          el.getAttribute('val') ||
          ''
        );
      }
    }
  }
  return null;
}

// ---------------------------------------------------------------------------
// Character mapping tables
// ---------------------------------------------------------------------------

/** Map special Unicode characters to LaTeX commands.
 *
 * When a mapped command ends with a letter (e.g., `\cdot`, `\leq`, `\alpha`)
 * and the NEXT character is also a letter, we must insert a space so KaTeX
 * doesn't interpret them as a single unknown command (e.g., `\cdotR` → `\cdot R`).
 */
function mapSpecialChars(text: string): string {
  let result = '';
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    const mapped = CHAR_MAP[ch];
    if (mapped) {
      result += mapped;
      // If the mapped command ends with a letter and the next char is also a letter,
      // insert a space to prevent KaTeX from merging them (e.g., \cdotR → \cdot R)
      const nextCh = i + 1 < text.length ? text[i + 1] : '';
      if (
        mapped.length > 1 &&
        /[a-zA-Z]$/.test(mapped) &&
        /[a-zA-Z]/.test(nextCh)
      ) {
        result += ' ';
      }
    } else {
      result += ch;
    }
  }
  return result;
}

/** Map delimiter characters to LaTeX \left/\right-compatible forms. */
function mapDelimiter(char: string): string {
  if (!char || char === '') return '.'; // invisible delimiter
  const map: Record<string, string> = {
    '(': '(',
    ')': ')',
    '[': '[',
    ']': ']',
    '{': '\\{',
    '}': '\\}',
    '|': '|',
    '\u2016': '\\|', // double vertical bar
    '\u2329': '\\langle',
    '\u232A': '\\rangle',
    '\u27E8': '\\langle',
    '\u27E9': '\\rangle',
    '\u230A': '\\lfloor',
    '\u230B': '\\rfloor',
    '\u2308': '\\lceil',
    '\u2309': '\\rceil',
  };
  return map[char] || char;
}

/** Map n-ary operator characters to LaTeX commands. */
function mapNaryOperator(char: string): string {
  const map: Record<string, string> = {
    '\u2211': '\\sum',
    '\u220F': '\\prod',
    '\u2210': '\\coprod',
    '\u222B': '\\int',
    '\u222C': '\\iint',
    '\u222D': '\\iiint',
    '\u222E': '\\oint',
    '\u22C0': '\\bigwedge',
    '\u22C1': '\\bigvee',
    '\u22C2': '\\bigcap',
    '\u22C3': '\\bigcup',
    '\u2A00': '\\bigoplus',
    '\u2A01': '\\bigoplus',
    '\u2A02': '\\bigotimes',
  };
  return map[char] || '\\int';
}

/** Map accent combining characters to LaTeX accent commands. */
function mapAccentChar(char: string): string {
  const map: Record<string, string> = {
    '\u0302': '\\hat', // circumflex
    '\u0303': '\\tilde', // tilde
    '\u0300': '\\grave', // grave
    '\u0301': '\\acute', // acute
    '\u0304': '\\bar', // macron
    '\u0306': '\\breve', // breve
    '\u0307': '\\dot', // dot above
    '\u0308': '\\ddot', // double dot
    '\u20D7': '\\vec', // vector arrow
    '\u030C': '\\check', // caron
    '\u0305': '\\overline', // overline
    '\u0332': '\\underline', // underline
  };
  return map[char] || '\\hat';
}

/** Map common function name text to LaTeX commands. */
function mapFunctionName(name: string): string | null {
  // Strip LaTeX wrappers that the run converter may have added (e.g., \mathrm{sin})
  let cleaned = name.trim();
  const wrapperMatch = cleaned.match(
    /^\\(?:mathrm|mathbf|text|operatorname)\{(.+)\}$/
  );
  if (wrapperMatch) cleaned = wrapperMatch[1];
  const lower = cleaned.toLowerCase().replace(/\\/g, '').trim();
  const map: Record<string, string> = {
    sin: '\\sin',
    cos: '\\cos',
    tan: '\\tan',
    csc: '\\csc',
    sec: '\\sec',
    cot: '\\cot',
    sinh: '\\sinh',
    cosh: '\\cosh',
    tanh: '\\tanh',
    log: '\\log',
    ln: '\\ln',
    lg: '\\lg',
    exp: '\\exp',
    lim: '\\lim',
    min: '\\min',
    max: '\\max',
    inf: '\\inf',
    sup: '\\sup',
    det: '\\det',
    dim: '\\dim',
    gcd: '\\gcd',
    hom: '\\hom',
    ker: '\\ker',
    arg: '\\arg',
    deg: '\\deg',
    mod: '\\bmod',
    Pr: '\\Pr',
  };
  return map[lower] || null;
}

/** Unicode → LaTeX character map for common math symbols. */
const CHAR_MAP: Record<string, string> = {
  // Greek uppercase
  '\u0393': '\\Gamma',
  '\u0394': '\\Delta',
  '\u0398': '\\Theta',
  '\u039B': '\\Lambda',
  '\u039E': '\\Xi',
  '\u03A0': '\\Pi',
  '\u03A3': '\\Sigma',
  '\u03A5': '\\Upsilon',
  '\u03A6': '\\Phi',
  '\u03A8': '\\Psi',
  '\u03A9': '\\Omega',

  // Greek lowercase
  '\u03B1': '\\alpha',
  '\u03B2': '\\beta',
  '\u03B3': '\\gamma',
  '\u03B4': '\\delta',
  '\u03B5': '\\epsilon',
  '\u03B6': '\\zeta',
  '\u03B7': '\\eta',
  '\u03B8': '\\theta',
  '\u03B9': '\\iota',
  '\u03BA': '\\kappa',
  '\u03BB': '\\lambda',
  '\u03BC': '\\mu',
  '\u03BD': '\\nu',
  '\u03BE': '\\xi',
  '\u03C0': '\\pi',
  '\u03C1': '\\rho',
  '\u03C2': '\\varsigma',
  '\u03C3': '\\sigma',
  '\u03C4': '\\tau',
  '\u03C5': '\\upsilon',
  '\u03C6': '\\phi',
  '\u03C7': '\\chi',
  '\u03C8': '\\psi',
  '\u03C9': '\\omega',

  // Greek variants
  '\u03D1': '\\vartheta',
  '\u03D5': '\\varphi',
  '\u03D6': '\\varpi',
  '\u03F1': '\\varrho',
  '\u03F5': '\\varepsilon',

  // Operators
  '\u00B1': '\\pm',
  '\u2213': '\\mp',
  '\u00D7': '\\times',
  '\u00F7': '\\div',
  '\u22C5': '\\cdot',
  '\u2217': '\\ast',
  '\u2218': '\\circ',
  '\u2219': '\\bullet',
  '\u2227': '\\wedge',
  '\u2228': '\\vee',
  '\u2229': '\\cap',
  '\u222A': '\\cup',
  '\u2295': '\\oplus',
  '\u2297': '\\otimes',

  // Relations
  '\u2264': '\\leq',
  '\u2265': '\\geq',
  '\u226A': '\\ll',
  '\u226B': '\\gg',
  '\u2260': '\\neq',
  '\u2248': '\\approx',
  '\u2261': '\\equiv',
  '\u2282': '\\subset',
  '\u2283': '\\supset',
  '\u2286': '\\subseteq',
  '\u2287': '\\supseteq',
  '\u2208': '\\in',
  '\u2209': '\\notin',
  '\u220B': '\\ni',
  '\u221D': '\\propto',
  '\u2245': '\\cong',
  '\u223C': '\\sim',
  '\u2AAF': '\\preceq',
  '\u2AB0': '\\succeq',
  '\u22A2': '\\vdash',
  '\u22A3': '\\dashv',
  '\u22A8': '\\models',

  // Arrows
  '\u2190': '\\leftarrow',
  '\u2191': '\\uparrow',
  '\u2192': '\\rightarrow',
  '\u2193': '\\downarrow',
  '\u2194': '\\leftrightarrow',
  '\u21D0': '\\Leftarrow',
  '\u21D1': '\\Uparrow',
  '\u21D2': '\\Rightarrow',
  '\u21D3': '\\Downarrow',
  '\u21D4': '\\Leftrightarrow',
  '\u21A6': '\\mapsto',
  '\u2197': '\\nearrow',
  '\u2198': '\\searrow',
  '\u21AA': '\\hookrightarrow',

  // Miscellaneous
  '\u221E': '\\infty',
  '\u2200': '\\forall',
  '\u2203': '\\exists',
  '\u2204': '\\nexists',
  '\u2205': '\\emptyset',
  '\u2207': '\\nabla',
  '\u2202': '\\partial',
  '\u210F': '\\hbar',
  '\u2111': '\\Im',
  '\u211C': '\\Re',
  '\u2135': '\\aleph',
  '\u2026': '\\ldots',
  '\u22EF': '\\cdots',
  '\u22EE': '\\vdots',
  '\u22F1': '\\ddots',
  '\u2032': "'", // prime
  '\u2033': "''", // double prime
  '\u00B0': '^{\\circ}', // degree
  '\u2220': '\\angle',
  '\u22A5': '\\perp',
  '\u2225': '\\parallel',
  '\u2234': '\\therefore',
  '\u2235': '\\because',
  '\u25A1': '\\square',
  '\u25B3': '\\triangle',
  '\u2605': '\\star',

  // Spacing
  '\u2009': '\\,', // thin space
  '\u2005': '\\;', // medium space
  '\u2003': '\\quad', // em space
};
